<script>
import { isEqual, get } from 'lodash';

import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';

import { CC_GATEWAYS, GATEWAYS, PAYMENT_METHODS, donationStatusIsAtLeast, sleep, widgetViews } from '@/utils';
import { extractData } from '@/services/payment-request';
import AppButton from '@/common/AppButton.vue';
import TotalDonation from '@/components/TotalDonation.vue';
import EntityCallOut from '@/components/EntityCallOut.vue';
import StripeService from '@/services/stripe';
import PayPalService from '@/services/paypal';
import captchaService from '@/services/captcha';

import { getSentryScope } from '@/services/sentry';

const ZELLE_ORG_ALLOWLIST = ['42b9a400-4092-11ef-bebc-bbffbfc6581b']; // Yaldei
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development' || process.env.VUE_APP_DEPLOY_BRANCH === 'develop';
const DONATION_FREQUENCY_LABELS = {
  single: 'One-time',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly'
};

const PAYMENT_REQUEST_METHODS = {
  'apple-pay': PAYMENT_METHODS.APPLE_PAY,
  'google-pay': PAYMENT_METHODS.GPAY,
  'https://google.com/pay': PAYMENT_METHODS.GPAY
};

const WALLET_DEBUG_QUERY_PARAM = 'doubleWalletDebug';

function isWalletDebugEnabled() {
  try {
    return new URLSearchParams(window.location.search).get(WALLET_DEBUG_QUERY_PARAM) === '1';
  } catch (_error) {
    return false;
  }
}

function isPaymentPolicyAllowed(documentObject) {
  try {
    const policy = documentObject?.permissionsPolicy || documentObject?.featurePolicy;
    return typeof policy?.allowsFeature === 'function' ? policy.allowsFeature('payment') : null;
  } catch (_error) {
    return null;
  }
}

function normalizeWalletResult(result) {
  if (!result) {
    return null;
  }

  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => {
      const normalizedValue = ['boolean', 'number', 'string'].includes(typeof value) || value === null ? value : String(value);
      return [key, normalizedValue];
    })
  );
}

export default {
  name: 'FormPaymentButtons',
  components: {
    AppButton,
    TotalDonation,
    EntityCallOut
  },

  data() {
    return {
      error: null,
      donationDirty: true, // Is local donation data different from the data on the server?
      paymentRequest: null,
      canMakePayment: null,
      loadingWallets: true,
      loadingPayPal: true,
      loadingVenmo: true,
      showVenmoButton: false,
      walletRequestSequence: 0,
      walletRequestsInFlight: 0,
      GATEWAYS,
      PAYMENT_METHODS,
      isChariotProcessing: false,
      isMounted: false,
      paypalButtonsRendered: false // Track if PayPal buttons are already rendered
    };
  },

  computed: {
    ...mapState(['orgId', 'isWidgetOpen', 'showPaymentButtons']),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapGetters('donation', [
      'amountPlusFees',
      'totalDonation',
      'persistentDonationData',
      'isPayPalAvailable',
      'hasEndDate',
      'isRecurring',
      'isStartDateToday',
      'isCharityCard'
    ]),
    ...mapGetters('campaign', ['isOnlyDafEnabled']),
    ...mapGetters('publicOrgSettings', ['enhancedBotProtection']),
    ...mapGetters('validation', ['isFirstStepValid']),
    ...mapState('publicOrgSettings', {
      fees: (state) => state.settings.fees,
      chariotCid: (state) => state.chariotCid
    }),
    ...mapState('campaign', ['settings']),
    zelleGateway() {
      return this.$store.state.publicOrgSettings.gateways?.find((gateway) => gateway.provider === GATEWAYS.ZELLE && gateway.currency === 'usd');
    },
    showZelleButton() {
      return (
        (IS_DEVELOPMENT || ZELLE_ORG_ALLOWLIST.includes(this.orgId)) &&
        !!this.zelleGateway &&
        this.donation.currency === 'usd' &&
        this.donation.frequency === 'single' &&
        this.isStartDateToday &&
        !this.settings.disablePaymentMethods?.zelle
      );
    },
    isGooglePayAvailable() {
      if (this.settings.disablePaymentMethods?.appleGooglePay) {
        return false;
      }
      return this.canMakePayment?.googlePay;
    },
    isApplePayAvailable() {
      if (this.settings.disablePaymentMethods?.appleGooglePay) {
        return false;
      }
      return this.canMakePayment?.applePay;
    },
    showChariot() {
      if (this.settings.disablePaymentMethods?.[GATEWAYS.CHARIOT]) {
        return false;
      }
      const supportedCurrency = this.donation.currency === 'usd';
      const supportedFrequency = this.donation.frequency === 'single';
      return supportedCurrency && supportedFrequency && this.isStartDateToday;
    },
    isDailyGiving() {
      const DAILY_GIVING_MONTHLY_DAF_CAMPAIGN_ID = '8b99fde0-81c9-11f0-b2c9-f530e006e910';
      return this.donation.campaignId === DAILY_GIVING_MONTHLY_DAF_CAMPAIGN_ID;
    },
    showPaymentRequestButton() {
      return this.donation.CCGateway.provider === GATEWAYS.STRIPE;
    },
    showCCButton() {
      // {"appleGoogle": true, "cards": true, "paypal": true, "ach": true, "chariot":true}
      if (this.settings.disablePaymentMethods?.[PAYMENT_METHODS.CARD]) {
        return false;
      }
      return !!this.donation.CCGateway?.isCcGateway;
    },
    showBankButton() {
      if (this.donation.currency?.toLowerCase() !== 'usd') {
        return false;
      }
      if (this.settings.disablePaymentMethods?.[PAYMENT_METHODS.ACH]) {
        return false;
      }
      return this.donation.CCGateway.provider === GATEWAYS.STRIPE || this.donation.CCGateway.provider === GATEWAYS.CARDKNOX;
    },
    showPaypalButton() {
      if (!this.isPayPalAvailable) {
        return false;
      }
      if (this.settings.disablePaymentMethods?.[GATEWAYS.PAYPAL]) {
        return false;
      }

      // Till we implement the conversion handling
      if (this.donation.currency !== 'usd') {
        return false;
      }
      // temp for "Machne Israel" show only for "Keren Hashana campaign
      if (this.orgId === 'da07cc30-5318-11f0-b670-0de679036343' && this.donation.campaignId !== 'de3902f0-8361-11f0-ab44-65b5075014bb') {
        return false;
      }

      return true;
    },

    showBitButton() {
      if (this.isRecurring) return false;
      return this.donation.CCGateway.provider === GATEWAYS.GROW;
    },
    cardknoxGateway() {
      return this.$store.state.publicOrgSettings.gateways?.find(
        (g) => g.provider === GATEWAYS.CARDKNOX && g.isCcGateway && g.currency === this.donation.currency
      );
    },
    showCharityCardButton() {
      if (this.settings.disablePaymentMethods?.[PAYMENT_METHODS.DAF]) {
        return false;
      }
      return !!this.cardknoxGateway;
    },
    stripePaymentSucceeded() {
      return get(this.donation, 'stripeIntent.status') === 'succeeded';
    }
  },

  watch: {
    // We must handle donation details being changed after the initial submission to the server
    persistentDonationData(newValue, oldValue) {
      if (!isEqual(newValue, oldValue)) {
        if (this.stripePaymentSucceeded) {
          return;
        }
        this.donationDirty = true;
      }
    },
    'donation.currency'() {
      this.createStripeRequest('currency-change');
      this.paypalButtonsRendered = false; // Reset flag to allow re-rendering with new currency
      this.createPayPalButton();
    },

    totalDonation() {
      if (!this.paymentRequest) {
        return;
      }
      this.paymentRequest.update({
        total: {
          label: 'Your donation',
          amount: Math.round(this.amountPlusFees * 100)
        }
      });
    },
    'donation.paypalGateway': {
      handler(newValue) {
        if (newValue) {
          this.paypalButtonsRendered = false; // Reset flag when gateway changes
          this.$nextTick(() => {
            this.createPayPalButton();
          });
        }
      },
      immediate: true
    },
    // Re-run createStripeRequest when CCGateway becomes available (handles race condition on mount)
    'donation.CCGateway': {
      handler(newValue) {
        if (newValue?.provider === CC_GATEWAYS.STRIPE && !this.paymentRequest) {
          this.createStripeRequest('gateway-change');
        }
      },
      immediate: false
    }
  },

  methods: {
    ...mapActions('donation', ['createDonation', 'confirmDonation', 'confirmStripePaymentRequest', 'donateWithChariot', 'donateWithPayPal', 'paypalIntent']),
    ...mapMutations(['SET_WIDGET_VIEW', 'SET_SUCCESSFULLY_DONATED', 'SET_SHOW_PAYMENT_BUTTONS']),
    ...mapMutations('donation', [
      'SET_AMOUNT',
      'SET_PAY_COVER_FEES',
      'SET_DONOR_DETAILS',
      'SET_BILLING_ADDRESS',
      'SET_GATEWAY',
      'SET_CC_GATEWAY',
      'SET_PAYMENT_METHOD',
      'SET_CHARIOT_INTENT'
    ]),
    ...mapMutations('validation', ['SET_IS_PAYMENT_DETAILS_VALID']),

    setupChariot() {
      const chariot = this.$refs.chariot;
      if (!chariot || chariot.__doubleInitialized) {
        return;
      }

      const initialize = () => {
        if (chariot.__doubleInitialized || typeof chariot.onDonationRequest !== 'function') {
          return;
        }

        chariot.__doubleInitialized = true;
        chariot.removeEventListener('CHARIOT_INIT', initialize);
        chariot.onDonationRequest(async () => {
          this.isChariotProcessing = true;
          await sleep(5); // wait for the cover fees to be updated, no access here to $nextTick
          const frequency = this.donation.frequency === 'monthly' ? 'MONTHLY' : 'ONE_TIME';
          return {
            amount: Math.ceil(this.amountPlusFees * 100),
            frequency,
            ...(this.isDailyGiving && { lockAmount: true })
          };
        });

        chariot.addEventListener('CHARIOT_SUCCESS', this.chariotSucceeded);
        chariot.addEventListener('CHARIOT_EXIT', (event) => {
          this.isChariotProcessing = false;
          this.$nextTick(() => this.setupChariot());
          getSentryScope().captureMessage(`CHARIOT_EXIT: Reason: ${event.detail?.reason}. Description: ${event.detail?.description}`, 'info');
        });
      };

      chariot.addEventListener('CHARIOT_INIT', initialize);
      initialize();
    },

    async donateWithPaymentRequest(paymentMethodEvent) {
      this.SET_PAYMENT_METHOD(PAYMENT_REQUEST_METHODS[paymentMethodEvent.methodName]);

      try {
        const { firstName, lastName, email, address, phone } = extractData(paymentMethodEvent);
        this.SET_DONOR_DETAILS({ companyName: this.donation.donorDetails.companyName, firstName, lastName, email, phone });
        this.SET_BILLING_ADDRESS(address);
        await this.$nextTick(); // wait for SET_DONOR_DETAILS to update
        // Step 1 - Create the payment intent
        if (this.donationDirty && !this.stripePaymentSucceeded) {
          await this.createDonation();
          this.donationDirty = false;
        }
        // Step 2 - Confirm the payment intent
        if (!this.stripePaymentSucceeded) {
          await this.confirmStripePaymentRequest(paymentMethodEvent.paymentMethod.id);
        }
        // Step 3 - Confirm the donation on our server
        const { status, error } = await this.confirmDonation();
        if (error) {
          throw error;
        }
        if (!error && donationStatusIsAtLeast(status, 'subscriptionScheduled')) {
          this.paymentSucceeded();
        }
        paymentMethodEvent.complete('success');
      } catch (error) {
        paymentMethodEvent.complete('fail');
        this.error = error.message || error || 'An error occurred';
        getSentryScope().captureMessage(`Payment request failed: ${error}`, 'info');
      }
    },

    getWalletEnvironment() {
      const iframe = document.querySelector('iframe[name="double-checkout"]');
      let iframeSecureContext = null;
      let iframePaymentPolicyAllowed = null;
      let iframeOrigin = null;

      try {
        iframeSecureContext = iframe?.contentWindow?.isSecureContext ?? null;
        iframePaymentPolicyAllowed = isPaymentPolicyAllowed(iframe?.contentDocument);
        iframeOrigin = iframe?.contentWindow?.location?.origin || null;
      } catch (_error) {
        // Cross-origin iframe details are intentionally reported as null.
      }

      return {
        paymentRequestSupported: typeof window.PaymentRequest === 'function',
        secureContext: window.isSecureContext,
        paymentPolicyAllowed: isPaymentPolicyAllowed(document),
        documentVisibility: document.visibilityState,
        navigatorOnline: navigator.onLine,
        topLevelOrigin: window.location.origin,
        iframe: {
          exists: Boolean(iframe),
          src: iframe?.getAttribute('src') || null,
          allow: iframe?.getAttribute('allow') || null,
          allowPaymentRequest: iframe?.getAttribute('allowpaymentrequest') || null,
          secureContext: iframeSecureContext,
          paymentPolicyAllowed: iframePaymentPolicyAllowed,
          origin: iframeOrigin
        }
      };
    },

    buildWalletContext({ attemptId, trigger, decision, startedAt, startedCurrency, startedGatewayProvider, startedStripeAccount, ...details }) {
      return {
        event: 'stripe_wallet_availability',
        attemptId,
        trigger,
        decision,
        durationMs: Date.now() - startedAt,
        startedCurrency,
        currentCurrency: this.donation.currency,
        gatewayProvider: startedGatewayProvider,
        stripeAccount: startedStripeAccount,
        currentStripeAccount: this.donation.CCGateway?.keys?.stripe_user_id || null,
        orgId: this.orgId,
        campaignId: this.donation.campaignId,
        frequency: this.donation.frequency,
        amountInMinorUnits: Math.round(this.amountPlusFees * 100),
        isWidgetOpen: this.isWidgetOpen,
        showPaymentButtons: this.showPaymentButtons,
        walletDisabledByCampaign: Boolean(this.settings.disablePaymentMethods?.appleGooglePay),
        environment: this.getWalletEnvironment(),
        ...details
      };
    },

    reportWalletAvailability(context, { capture = false, level = 'info' } = {}) {
      const sentryScope = getSentryScope();
      const debugEnabled = isWalletDebugEnabled();
      const shouldCapture = capture || debugEnabled;

      sentryScope.setContext('stripeWalletAvailability', context);
      sentryScope.setTag('walletDecision', context.decision);
      sentryScope.addBreadcrumb({
        category: 'stripe-wallet',
        message: `Stripe wallet availability: ${context.decision}`,
        level,
        data: context
      });

      if (debugEnabled) {
        console.info('[Double wallet availability]', context);
      }

      if (shouldCapture) {
        sentryScope.captureMessage(`Stripe wallet availability: ${context.decision}`, level);
      }
    },

    async createStripeRequest(trigger = 'unknown') {
      const startedAt = Date.now();
      const attemptNumber = ++this.walletRequestSequence;
      const attemptId = `${startedAt}-${attemptNumber}`;
      const startedCurrency = this.donation.currency;
      const startedGatewayProvider = this.donation.CCGateway?.provider || null;
      const startedStripeAccount = this.donation.CCGateway?.keys?.stripe_user_id || null;

      if (!this.donation.CCGateway?.provider) {
        this.reportWalletAvailability(
          this.buildWalletContext({
            attemptId,
            trigger,
            decision: 'gateway-not-ready',
            startedAt,
            startedCurrency,
            startedGatewayProvider,
            startedStripeAccount,
            result: null
          })
        );
        return;
      }

      if (this.donation.CCGateway.provider !== CC_GATEWAYS.STRIPE) {
        this.reportWalletAvailability(
          this.buildWalletContext({
            attemptId,
            trigger,
            decision: 'non-stripe-gateway',
            startedAt,
            startedCurrency,
            startedGatewayProvider,
            startedStripeAccount,
            result: null
          })
        );
        return;
      }

      this.walletRequestsInFlight += 1;
      const concurrentAttemptCount = this.walletRequestsInFlight;
      let phase = 'stripe-load';

      try {
        // Important: Create Stripe instance from outside the iframe
        const stripe = await StripeService.getStripeObject(true);

        if (!stripe) {
          this.reportWalletAvailability(
            this.buildWalletContext({
              attemptId,
              trigger,
              decision: 'stripe-load-failed',
              startedAt,
              startedCurrency,
              startedGatewayProvider,
              startedStripeAccount,
              concurrentAttemptCount,
              result: null
            }),
            { capture: true, level: 'warning' }
          );
          this.loadingWallets = false;
          return;
        }

        phase = 'payment-request-create';
        this.paymentRequest = stripe.paymentRequest({
          country: 'US',
          currency: this.donation.currency,
          total: {
            label: 'Your donation',
            amount: Math.round(this.amountPlusFees * 100)
          },
          requestPayerName: true,
          requestPayerEmail: true,
          requestPayerPhone: Boolean(this.settings.optionalFields?.phoneNumber?.collect)
        });

        this.paymentRequest.on('paymentmethod', this.donateWithPaymentRequest);

        phase = 'can-make-payment';
        this.canMakePayment = await this.paymentRequest.canMakePayment();

        const result = normalizeWalletResult(this.canMakePayment);
        const currentStripeAccount = this.donation.CCGateway?.keys?.stripe_user_id || null;
        const staleResult = startedCurrency !== this.donation.currency || startedStripeAccount !== currentStripeAccount;
        let decision = 'no-supported-wallet';

        if (staleResult) {
          decision = 'stale-result-after-context-change';
        } else if (this.settings.disablePaymentMethods?.appleGooglePay) {
          decision = 'campaign-disabled';
        } else if (result?.googlePay) {
          decision = 'google-pay';
        } else if (result?.applePay) {
          decision = 'apple-pay';
        } else if (result?.link) {
          decision = 'link-not-rendered';
        } else if (!result) {
          decision = 'can-make-payment-null';
        }

        const context = this.buildWalletContext({
          attemptId,
          trigger,
          decision,
          startedAt,
          startedCurrency,
          startedGatewayProvider,
          startedStripeAccount,
          concurrentAttemptCount,
          result
        });

        this.reportWalletAvailability(context, {
          capture: !['google-pay', 'apple-pay', 'campaign-disabled'].includes(decision) || concurrentAttemptCount > 1,
          level: decision === 'stale-result-after-context-change' ? 'warning' : 'info'
        });

        this.loadingWallets = false;
      } catch (error) {
        this.reportWalletAvailability(
          this.buildWalletContext({
            attemptId,
            trigger,
            decision: 'can-make-payment-failed',
            startedAt,
            startedCurrency,
            startedGatewayProvider,
            startedStripeAccount,
            phase,
            errorName: error?.name || null,
            errorMessage: error?.message || String(error),
            errorStack: error?.stack || null,
            concurrentAttemptCount,
            result: null
          }),
          { capture: true, level: 'error' }
        );
        this.loadingWallets = false;
      } finally {
        this.walletRequestsInFlight = Math.max(0, this.walletRequestsInFlight - 1);
      }
    },

    onPaymentRequestClicked() {
      if (!this.isGooglePayAvailable && !this.isApplePayAvailable) {
        return;
      }

      this.SET_GATEWAY(GATEWAYS.STRIPE);
      const paymentMethod = this.isApplePayAvailable ? PAYMENT_METHODS.APPLE_PAY : PAYMENT_METHODS.GPAY;
      this.selectPaymentMethod(paymentMethod);
    },
    selectZelle() {
      if (!this.isFirstStepValid) return;
      this.SET_GATEWAY(GATEWAYS.ZELLE);
      this.selectPaymentMethod(PAYMENT_METHODS.ZELLE);
    },
    selectPaymentMethod(methodName) {
      this.$emit('payment-method-selected');
      if (!this.isFirstStepValid) {
        return;
      }

      this.SET_PAYMENT_METHOD(methodName);
      if ([PAYMENT_METHODS.ZELLE, PAYMENT_METHODS.CARD, PAYMENT_METHODS.ACH, PAYMENT_METHODS.BANK_WALLET].includes(methodName) || this.isCharityCard) {
        this.SET_SHOW_PAYMENT_BUTTONS(false);
        this.SET_WIDGET_VIEW(widgetViews.CREDIT_CARD_FORM);
      } else if (methodName === PAYMENT_METHODS.APPLE_PAY || methodName === PAYMENT_METHODS.GPAY) {
        this.paymentRequest.show();
      }
    },
    selectCharityCard() {
      this.SET_CC_GATEWAY(this.cardknoxGateway);
      this.SET_GATEWAY(GATEWAYS.CARDKNOX);
      this.selectPaymentMethod(PAYMENT_METHODS.DAF);
    },

    async createPayPalButton() {
      if (!this.showPaypalButton) {
        return;
      }

      if (!this.donation.paypalGateway?.keys?.merchantId) {
        return;
      }

      const paypalButton = this.$refs['paypal-button'];
      const venmoButton = this.$refs['venmo-button'];

      if (!paypalButton) {
        return;
      }

      // Prevent duplicate renders if buttons are already rendered
      if (this.paypalButtonsRendered && !this.loadingPayPal && !this.loadingVenmo) {
        return;
      }

      try {
        this.loadingPayPal = true;
        this.loadingVenmo = true;

        // Clear existing PayPal button content to prevent duplicates
        paypalButton.innerHTML = '';
        if (venmoButton) {
          venmoButton.innerHTML = '';
        }

        // Load PayPal SDK once
        const paypal = await PayPalService.loadPaypalSDK(this.donation.paypalGateway.keys, this.donation.currency);

        const callbacks = {
          // appSwitchWhenAvailable: true,
          createOrder: async () => {
            this.SET_GATEWAY(GATEWAYS.PAYPAL);

            try {
              return await this.paypalIntent({ orgId: this.orgId, donation: this.donation });
            } catch (error) {
              this.error = error.message || 'An error occurred';
              getSentryScope().captureMessage(`PayPal donation failed: ${error}`, 'info');
            }
          },
          onApprove: async (payload) => {
            try {
              await this.donateWithPayPal(payload.orderID);
              this.paymentSucceeded();
            } catch (error) {
              this.error = error.message || 'An error occurred';
              getSentryScope().captureMessage(`PayPal donation failed: ${error}`, 'info');
            }
          },
          onError: (error) => {
            console.error('PayPal error:', error);
          }
        };

        // Render PayPal button
        await PayPalService.renderPayPalButton(paypal, callbacks, paypalButton);
        this.loadingPayPal = false;

        // Render Venmo button if available
        if (venmoButton) {
          const venmoRendered = await PayPalService.renderVenmoButton(paypal, callbacks, venmoButton);
          this.showVenmoButton = venmoRendered !== false;
        }
        this.loadingVenmo = false;

        // Mark buttons as rendered to prevent duplicates
        this.paypalButtonsRendered = true;
      } catch (error) {
        console.error('PayPal button creation failed:', error);
        this.loadingPayPal = false;
        this.loadingVenmo = false;
        this.paypalButtonsRendered = false;
      }
    },

    async chariotSucceeded({ detail }) {
      this.isChariotProcessing = true;

      const grantIntent = this.donation.frequency === 'monthly' ? detail.recurringGrantIntent : detail.grantIntent;
      const user = detail.user;
      if (!grantIntent) {
        // error handling
        throw new Error('No grantIntent found');
      }

      const hasAmountChanged = grantIntent.amount / 100 !== this.amountPlusFees;

      if (hasAmountChanged) {
        this.SET_AMOUNT(grantIntent.amount / 100);
        this.SET_PAY_COVER_FEES(false);
      }

      this.SET_CHARIOT_INTENT(detail);
      this.SET_DONOR_DETAILS({
        companyName: this.donation.donorDetails.companyName,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      });
      this.SET_BILLING_ADDRESS({
        city: user.address.city,
        country: user.address.country,
        line1: user.address.line1,
        line2: user.address.line2,
        zip: user.address.postalCode,
        state: user.address.state
      });

      try {
        await this.donateWithChariot({ workflowSessionId: detail.workflowSessionId, chariotAmount: grantIntent.amount });
        this.paymentSucceeded();
      } catch (error) {
        this.error = error.message || 'An error occurred';
        getSentryScope().captureMessage(`Chariot donation failed: ${error}`, 'info');
      } finally {
        this.isChariotProcessing = false;
        this.$nextTick(() => this.setupChariot());
      }
    },
    paymentSucceeded() {
      this.SET_WIDGET_VIEW(widgetViews.CONFIRMATION_DONATION);
      this.$doubleEvent('Double.donationCompleted');
      this.$trackEvent({
        action: 'success',
        label: DONATION_FREQUENCY_LABELS[this.donation.frequency] + ' donation',
        value: this.totalDonation
      });
      this.SET_SUCCESSFULLY_DONATED(true);
    }
  },

  async mounted() {
    this.isMounted = true;
    this.setupChariot();
    this.createStripeRequest('mounted'); // Move to created?
    // PayPal button will be created by the watcher when isPayPalAvailable becomes true

    // Initialize captcha for enhanced bot protection
    if (this.enhancedBotProtection) {
      try {
        await captchaService.initialize();
      } catch (error) {
        getSentryScope().captureMessage(`hCaptcha initialization failed: ${error.message || error}`, 'error');
        console.error('Failed to initialize hc:', error);
      }
    }
  }
};
</script>

<template>
  <app-popover :open="showPaymentButtons" title="Summary" @close="SET_SHOW_PAYMENT_BUTTONS(false)" class="form-payment-buttons">
    <total-donation />
    <div class="payment-buttons buttons w-full flex flex-col gap-3">
      <entity-call-out :gateway="donation.CCGateway" />
      <template v-if="showPaymentRequestButton">
        <button
          v-if="loadingWallets || isGooglePayAvailable || isApplePayAvailable"
          class="payment-request-button relative"
          :class="{ loading: loadingWallets }"
          @click="onPaymentRequestClicked"
        >
          <content-loader v-if="loadingWallets" class="absolute" />
          <app-icon v-else-if="isGooglePayAvailable" type="svg" name="g-pay" height="24" class="flex google-pay-button" />
          <div v-else-if="isApplePayAvailable" id="ckoApplePay" class="apple-pay-button apple-pay-button-text-donate" lang="en"></div>
        </button>
      </template>
      <app-button
        v-if="showCCButton"
        @click="
          selectPaymentMethod(PAYMENT_METHODS.CARD);
          SET_GATEWAY(donation.CCGateway.provider);
        "
        :disabled="!this.isFirstStepValid"
        class="payment-method flex items-center justify-center flex-1 gap-2.5 w-full bg-white"
        data-cy="card-button"
      >
        <app-icon type="svg" name="credit-card" width="22" class="fill-current text-black" />
        <h3>{{ $t('payment.card') }}</h3>
      </app-button>
      <app-button
        v-if="showCharityCardButton"
        @click="selectCharityCard"
        :disabled="!this.isFirstStepValid"
        class="payment-method flex items-center justify-center flex-1 gap-2.5 w-full bg-white"
        data-cy="charity-card-button"
      >
        <span class="charity-card-icons flex items-center">
          <app-icon type="svg" name="pledger" width="10" height="10" class="charity-card-icon" />
          <app-icon type="svg" name="matbia" width="10" height="10" class="charity-card-icon" />
          <app-icon type="svg" name="donors-fund" width="10" height="10" class="charity-card-icon" />
          <app-icon type="svg" name="ojc-fund" width="10" height="10" class="charity-card-icon" />
        </span>
        <h3>Charity card</h3>
      </app-button>
      <app-button
        v-if="showBankButton"
        class="payment-method bank-button flex items-center justify-center flex-1 gap-2.5 w-full bg-white"
        :disabled="!this.isFirstStepValid"
        @click="
          selectPaymentMethod(PAYMENT_METHODS.ACH);
          SET_GATEWAY(donation.CCGateway.provider);
        "
      >
        <app-icon type="svg" name="building-columns" width="22" class="fill-current text-black" />
        <h3>{{ $t('payment.bank') }}</h3>
      </app-button>
      <app-button v-if="showZelleButton" brand="zelle" @click="selectZelle" :disabled="!isFirstStepValid" class="payment-method w-full" data-cy="zelle-button">
        <img src="https://media.double.giving/images/zelle-payment.svg" alt="Zelle" width="49" height="20" />
      </app-button>
      <!-- Bit -->
      <div class="flex" v-if="showBitButton">
        <app-button
          style="background-color: rgb(0, 53, 59)"
          class="payment-method flex items-center justify-center flex-1 gap-2.5 w-full bg-white"
          @click="selectPaymentMethod(PAYMENT_METHODS.BANK_WALLET)"
        >
          <app-icon type="svg" name="bit" width="34" height="22" />
        </app-button>
        <app-button
          v-if="donation.CCGateway === GATEWAYS.STRIPE || donation.CCGateway === GATEWAYS.CARDKNOX"
          class="payment-method bank-button flex items-center justify-center flex-1 gap-2.5 w-full bg-white"
          :disabled="!this.isFirstStepValid"
          @click="
            selectPaymentMethod(PAYMENT_METHODS.ACH);
            SET_GATEWAY(donation.CCGateway.provider);
          "
        >
          <app-icon type="svg" name="building-columns" width="22" class="fill-current text-black" />
          <h3>{{ $t('payment.bank') }}</h3>
        </app-button>
      </div>

      <div v-show="showPaypalButton" class="app-button relative bg-white overflow-hidden rounded-lg">
        <content-loader v-if="loadingPayPal" class="absolute" />
        <div id="paypal-button" ref="paypal-button"></div>
      </div>
      <div v-show="showPaypalButton && showVenmoButton" class="app-button relative bg-white overflow-hidden rounded-lg">
        <content-loader v-if="loadingVenmo" class="absolute" />
        <div id="venmo-button" ref="venmo-button"></div>
      </div>
      <transition name="expand">
        <div v-if="chariotCid" v-show="showChariot" @click="SET_GATEWAY(GATEWAYS.CHARIOT)" class="chariot-wrapper rounded-lg overflow-hidden">
          <chariot-connect
            v-if="chariotCid && !isChariotProcessing"
            ref="chariot"
            :cid="chariotCid"
            id="chariot"
            class="h-12"
            theme="LightBlueTheme"
          ></chariot-connect>
          <template v-else-if="isChariotProcessing">
            <h3 class="text-white">{{ $t('common.wait') }}</h3>
            <app-loader />
          </template>
        </div>
      </transition>
    </div>
    <p v-if="error" class="text-sm text-red mt-1">{{ error }}</p>
  </app-popover>
</template>
